import type { EncodeObject } from '@cosmjs/proto-signing'
import type { StdFee } from '@cosmjs/stargate'

import type { QuoteWire } from '../../core/protocol/directRental'
import { DEFAULT_DEPLOYMENT_DEPOSIT_MICRO } from '../../core/guided'
import type { GroupSpecJson } from '../../core/protocol/convert'

/**
 * Everything a direct rental needs signed, from one SDL document.
 *
 * The invariant this function exists to hold: **the quote and the deployment
 * must describe the same group spec.** The chain recomputes
 * `GroupSpecHash(group.GroupSpec)` from the group as stored and compares it
 * with the hash the provider signed (ADR D6) — so if the SDL used to request
 * the quote differs by a single field from the SDL that becomes the
 * deployment, the transaction fails with `ErrQuoteSpecMismatch` after the
 * tenant has approved it.
 *
 * That is easy to get wrong by accident: two screens, two `parseSdl` calls, a
 * default filled in between them. So both derive from ONE `generatedSdl`
 * string, threaded through, and the quote request is built from the same
 * `groups` the deployment message carries.
 *
 * CosmJS is imported dynamically for the same reason `prepareDeployment` does
 * it: 1.4 MB is nine times the rest of the application, and a tenant reading a
 * review screen has not signed anything yet.
 */
export interface PreparedDirectRental {
  /** The two messages, deployment first. One `signAndBroadcast`. */
  messages: EncodeObject[]
  fee: StdFee
  dseq: bigint
  deposit: string
}

export interface QuoteRequestBody {
  tenant: string
  host_offer_id: string
  rental_mode: string
  group_spec: unknown
  dseq: number
  gseq: number
  oseq: number
}

/** GroupSpec.Price(), retained as the exact 18-decimal wire amount. */
export interface GroupPriceCeiling {
  denom: string
  amount: bigint
}

const DECIMAL_SCALE = 10n ** 18n

/**
 * Sum the first group's price × count exactly as the chain's GroupSpec.Price.
 *
 * The guided flow creates one group. Returning null for malformed or mixed
 * denominations makes the caller fail closed instead of comparing unlike
 * coins or silently dropping a replicated resource.
 */
export function groupCeilingPerBlock(groups: GroupSpecJson[]): GroupPriceCeiling | null {
  const resources = groups[0]?.resources ?? []
  if (resources.length === 0) return null
  const denom = resources[0]?.price.denom
  if (!denom) return null

  let amount = 0n
  for (const resource of resources) {
    if (resource.price.denom !== denom || !Number.isInteger(resource.count) || resource.count < 0) return null
    const raw = resource.price.amount
    if (!/^\d+\.\d{18}$/.test(raw)) return null
    amount += BigInt(raw.replace('.', '')) * BigInt(resource.count)
  }
  return { denom, amount }
}

function displayScaledAmount(amount: bigint): string {
  const whole = amount / DECIMAL_SCALE
  const fraction = (amount % DECIMAL_SCALE).toString().padStart(18, '0').replace(/0+$/, '')
  return fraction ? `${whole}.${fraction}` : whole.toString()
}

/**
 * Refuse to sign when the provider's live quote is above the GroupSpec.Price
 * ceiling derived from the exact parsed SDL being reviewed. The comparison is
 * integer per-block arithmetic so a quote can never cross the ceiling through
 * a floating-point conversion or a rounded hourly display.
 */
export function directQuotePriceError(ceiling: GroupPriceCeiling | null | undefined, quote: QuoteWire): string | null {
  const liveAmount = /^\d+$/.test(quote.price.amount) ? BigInt(quote.price.amount) : null
  if (!ceiling || liveAmount === null) {
    return 'The provider price could not be verified against the posted-price configuration. Refresh the price before signing.'
  }
  if (quote.price.denom !== ceiling.denom || liveAmount * DECIMAL_SCALE > ceiling.amount) {
    return `This provider now quotes ${quote.price.amount} ${quote.price.denom} per block, above the posted-price ceiling of ${displayScaledAmount(ceiling.amount)} ${ceiling.denom} per block. Refresh the provider price and try again.`
  }
  return null
}

/**
 * Build the quote request for an SDL document.
 *
 * The group spec goes to the provider as the same structure the deployment
 * will carry, so the provider hashes what the chain will store. Sending a
 * tenant-computed hash instead would ask the provider to agree to a number it
 * cannot verify — which is why the provider hashes the spec itself.
 */
export async function quoteRequestFor(args: {
  tenant: string
  hostOfferId: string
  rentalMode: string
  generatedSdl: string
  dseq: bigint
}): Promise<QuoteRequestBody & { priceCeiling: GroupPriceCeiling | null }> {
  const [{ parseSdl, validateSdl }, { sdlToDeployment }] = await Promise.all([
    import('../../core/sdl'),
    import('../../core/protocol/convert'),
  ])

  const doc = parseSdl(args.generatedSdl)
  const issues = validateSdl(doc).filter((issue) => issue.severity === 'error')
  if (issues.length > 0) {
    throw new Error(issues.map((issue) => `${issue.path}: ${issue.message}`).join(' '))
  }

  const { groups } = sdlToDeployment(doc)
  const group = groups[0]
  if (!group) throw new Error('This configuration has no deployment group to price.')

  return {
    tenant: args.tenant,
    host_offer_id: args.hostOfferId,
    rental_mode: args.rentalMode,
    group_spec: group,
    dseq: Number(args.dseq),
    // gseq and oseq are 1 for a single-group deployment, which is what the
    // guided flow produces. A multi-group SDL would need a group chosen, and
    // the guided flow cannot make one — so this is correct for every draft
    // that can reach here rather than a simplification.
    gseq: 1,
    oseq: 1,
    priceCeiling: groupCeilingPerBlock(groups),
  }
}

/**
 * Compose the signable transaction for a direct rental.
 *
 * Takes the SAME `generatedSdl` that produced the quote. Passing a different
 * one is the failure described at the top of this file, and the parameter name
 * is the only guard against it — which is why the quote is passed in rather
 * than fetched here: a function that both fetched a quote and built a
 * deployment could quietly use two different SDL strings.
 */
export async function prepareDirectRental(args: {
  address: string
  chainId: string
  denom: string
  height: number
  generatedSdl: string
  quote: QuoteWire
  /** The first escrow deposit in `uddt`; the chain minimum when omitted. */
  deposit?: string | undefined
}): Promise<PreparedDirectRental> {
  const [
    { parseSdl, validateSdl },
    { sdlToDeployment },
    { manifestVersion },
    { createDeploymentMessage },
    { registry },
    { feeFor, gasForDirectRental },
    { directRentalMessages },
  ] = await Promise.all([
    import('../../core/sdl'),
    import('../../core/protocol/convert'),
    import('../../core/protocol/manifest'),
    import('../../core/protocol/deployment'),
    import('../../core/tx'),
    import('../../core/fees'),
    import('../../core/protocol/directRental'),
  ])

  const doc = parseSdl(args.generatedSdl)
  const issues = validateSdl(doc).filter((issue) => issue.severity === 'error')
  if (issues.length > 0) {
    throw new Error(issues.map((issue) => `${issue.path}: ${issue.message}`).join(' '))
  }

  if (!args.height) {
    throw new Error('The chain did not report a block height, so this rental cannot be created yet.')
  }

  const { groups, manifest } = sdlToDeployment(doc)
  const priceError = directQuotePriceError(groupCeilingPerBlock(groups), args.quote)
  if (priceError) throw new Error(priceError)
  const dseq = BigInt(args.quote.dseq)
  const deposit = args.deposit ?? DEFAULT_DEPLOYMENT_DEPOSIT_MICRO

  const deploymentMessage = createDeploymentMessage({
    owner: args.address,
    dseq,
    groups,
    hash: manifestVersion(manifest),
    deposit: { denom: args.denom, amount: deposit },
  })

  // The composition validates the quote against this dseq and tenant, so a
  // mismatch throws here rather than at the wallet prompt.
  const messages = directRentalMessages({
    tenant: args.address,
    dseq,
    deploymentMessage,
    quote: args.quote,
  })

  // Gas for BOTH messages. The deployment's share scales with the encoded
  // group spec; the quote's is a constant. See fees.ts for why they are summed,
  // and why the fee itself is empty.
  const fee = feeFor(gasForDirectRental(registry.encode(deploymentMessage).length))

  return { messages, fee, dseq, deposit }
}
